import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '@/services/api';
import type { Database } from '@/types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];
type TaskInsert = Database['public']['Tables']['tasks']['Insert'];
type TaskUpdate = Database['public']['Tables']['tasks']['Update'];

/**
 * Hook to fetch all tasks for a project
 */
export const useTasks = (projectId: number) => {
    return useQuery({
        queryKey: ['tasks', projectId],
        queryFn: async () => {
            const { data, error } = await tasksApi.getByProject(projectId);
            if (error) throw error;
            return data ?? [];
        },
        enabled: !!projectId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch a single task by ID
 */
export const useTask = (id: number) => {
    return useQuery({
        queryKey: ['task', id],
        queryFn: async () => {
            const { data, error } = await tasksApi.getById(id);
            if (error) throw error;
            return data;
        },
        enabled: !!id,
    });
};

/**
 * Hook to create a new task
 */
export const useCreateTask = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (task: TaskInsert) => {
            const { data, error } = await tasksApi.create(task);
            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            // Invalidate tasks list for the project
            if (data?.project_id) {
                queryClient.invalidateQueries({ queryKey: ['tasks', data.project_id] });
            }
            // Also invalidate all tasks queries
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
        },
    });
};

/**
 * Hook to update an existing task
 */
export const useUpdateTask = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, updates }: { id: number; updates: TaskUpdate }) => {
            const { data, error } = await tasksApi.update(id, updates);
            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            // Invalidate both the list and the specific task
            if (data?.id) {
                queryClient.invalidateQueries({ queryKey: ['task', data.id] });
            }
            if (data?.project_id) {
                queryClient.invalidateQueries({ queryKey: ['tasks', data.project_id] });
            }
            // Also invalidate all tasks queries
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
        },
    });
};

/**
 * Hook to update task status with optimistic UI and rollback on error.
 * Invalidates both the project task list and any member-timeline queries.
 */
export const useUpdateTaskStatus = () => {
    const queryClient = useQueryClient();

    return useMutation<
        Task | null,
        Error,
        { taskId: number; status: Task['status']; projectId: number },
        { previousTasks: Task[] | undefined; projectId: number }
    >({
        mutationFn: async ({ taskId, status }) => {
            const { data, error } = await tasksApi.update(taskId, { status });
            if (error) throw error;
            return data;
        },
        onMutate: async ({ taskId, status, projectId }) => {
            await queryClient.cancelQueries({ queryKey: ['tasks', projectId] });
            const previousTasks = queryClient.getQueryData<Task[]>(['tasks', projectId]);
            if (previousTasks !== undefined) {
                queryClient.setQueryData<Task[]>(['tasks', projectId], (old) =>
                    old?.map((t) => (t.id === taskId ? { ...t, status } : t)) ?? []
                );
            }
            return { previousTasks, projectId };
        },
        onError: (_err, _vars, context) => {
            if (context?.previousTasks !== undefined) {
                queryClient.setQueryData(['tasks', context.projectId], context.previousTasks);
            }
        },
        onSettled: (_data, _err, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
            queryClient.invalidateQueries({ queryKey: ['member-tasks'] });
        },
    });
};

/**
 * Hook to delete a task
 */
export const useDeleteTask = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, projectId }: { id: number; projectId: number }) => {
            const { error } = await tasksApi.delete(id);
            if (error) throw error;
            return { id, projectId };
        },
        onSuccess: ({ projectId }) => {
            // Invalidate tasks list for the project
            queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
            // Also invalidate all tasks queries
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
        },
    });
};

/** Fetch all tasks across multiple projects (used for dashboard-level export). */
export const useAllTasks = (projectIds: number[]) => {
    return useQuery({
        queryKey: ['tasks', 'all', projectIds],
        queryFn: async () => {
            const { data, error } = await tasksApi.getByProjectIds(projectIds);
            if (error) throw error;
            return data ?? [];
        },
        enabled: projectIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });
};

